import { create } from 'zustand';

/**
 * Chat messages with colleagues (owner, 3 October 2026: "workspace should have the messaging
 * function"), one conversation per extension. Messages travel as SIP MESSAGE and the edge routes
 * them by extension to every device that shows them (OpenSIPS route[MESSAGE_ROUTER], 4 October
 * 2026), so the platform keeps no copy: like a phone's texts, the conversation is kept here, in
 * this browser, for this seat.
 */
export type MessageStatus = 'sending' | 'sent' | 'unavailable' | 'failed';
export interface ChatMessage { id: string; dir: 'in' | 'out'; text: string; at: number; status?: MessageStatus }
export interface Thread { ext: string; messages: ChatMessage[]; unread: number }

const PER_THREAD = 200;
const THREADS = 60;
const key = (seat: string): string => `arc.messages.${seat}`;

function read(seat: string): Record<string, Thread> {
  try {
    const raw = localStorage.getItem(key(seat));
    return raw ? (JSON.parse(raw) as Record<string, Thread>) : {};
  } catch { return {}; }
}

function write(seat: string, threads: Record<string, Thread>): void {
  // The newest conversations, each to its newest messages: a long-lived workspace must not grow
  // the browser's storage without end.
  const kept = Object.values(threads)
    .sort((a, b) => (b.messages.at(-1)?.at ?? 0) - (a.messages.at(-1)?.at ?? 0))
    .slice(0, THREADS)
    .map((t) => ({ ...t, messages: t.messages.slice(-PER_THREAD) }));
  try { localStorage.setItem(key(seat), JSON.stringify(Object.fromEntries(kept.map((t) => [t.ext, t])))); } catch { /* private window */ }
}

interface MessagesState {
  seat: string | null;
  threads: Record<string, Thread>;
  /** The conversation on screen: arriving messages there are read already. */
  open: string | null;
  load: (seat: string) => void;
  receive: (ext: string, text: string) => void;
  send: (ext: string, text: string) => string;
  settle: (ext: string, id: string, status: MessageStatus) => void;
  setOpen: (ext: string | null) => void;
}

export const useMessages = create<MessagesState>((set, get) => {
  const change = (fn: (t: Record<string, Thread>) => Record<string, Thread>): void => {
    const { seat, threads } = get();
    if (!seat) return;
    const next = fn(threads);
    write(seat, next);
    set({ threads: next });
  };
  const thread = (threads: Record<string, Thread>, ext: string): Thread => threads[ext] ?? { ext, messages: [], unread: 0 };
  return {
    seat: null,
    threads: {},
    open: null,
    load: (seat) => { if (get().seat !== seat) set({ seat, threads: read(seat) }); },
    receive: (ext, text) => change((all) => {
      const t = thread(all, ext);
      const id = `in-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      return { ...all, [ext]: { ...t, messages: [...t.messages, { id, dir: 'in', text, at: Date.now() }],
        unread: get().open === ext ? 0 : t.unread + 1 } };
    }),
    send: (ext, text) => {
      const id = `out-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      change((all) => {
        const t = thread(all, ext);
        return { ...all, [ext]: { ...t, messages: [...t.messages, { id, dir: 'out', text, at: Date.now(), status: 'sending' }] } };
      });
      return id;
    },
    settle: (ext, id, status) => change((all) => {
      const t = all[ext];
      if (!t) return all;
      return { ...all, [ext]: { ...t, messages: t.messages.map((m) => (m.id === id ? { ...m, status } : m)) } };
    }),
    setOpen: (ext) => {
      set({ open: ext });
      if (ext && (get().threads[ext]?.unread ?? 0) > 0) {
        change((all) => ({ ...all, [ext]: { ...all[ext]!, unread: 0 } }));
      }
    },
  };
});

// Another tab of the same workspace wrote: its messages show here too. Only one tab holds the
// phone (useBrowserPhone), so this is how the others keep up.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    const { seat } = useMessages.getState();
    if (seat && e.key === key(seat)) useMessages.setState({ threads: read(seat) });
  });
}

/** The extension a sender's SIP user belongs to: "2005-meridian", "web2005-meridian" or "2005". */
export function extensionOf(user: string): string {
  return user.replace(/^web/i, '').replace(/-.*$/, '');
}
